import type { ComposeSnapshot } from '../../../models/composeSnapshot';
import {
  isSnapshotSyncing,
  useComposeSnapshotIndexStore,
} from '../../../store/ComposeSnapshotIndex';
import {
  deleteComposeSnapshot,
  getComposeSnapshot,
} from '../../../store/utils/composeSnapshotCache';

export interface LocalDeletionPlan {
  localSessionIds: string[];
  serverDraftIds: string[];
  savingSessionIds: string[];
}

// A local row never has a server draft; an entry that somehow has one goes down the server path.
// A row whose save is in flight is skipped: deleting only the snapshot would orphan the draft the save is about to create.
export const planLocalDeletion = (sessionIds: string[]): LocalDeletionPlan => {
  const { entriesBySessionId } = useComposeSnapshotIndexStore.getState();
  const plan: LocalDeletionPlan = {
    localSessionIds: [],
    serverDraftIds: [],
    savingSessionIds: [],
  };
  for (const sessionId of sessionIds) {
    const entry = entriesBySessionId[sessionId];
    if (isSnapshotSyncing(sessionId)) {
      plan.savingSessionIds.push(sessionId);
    } else if (entry?.draftId) {
      plan.serverDraftIds.push(entry.draftId);
    } else {
      plan.localSessionIds.push(sessionId);
    }
  }
  return plan;
};

export const deleteLocalSnapshots = async (
  sessionIds: string[],
): Promise<ComposeSnapshot[]> => {
  const { userId } = useComposeSnapshotIndexStore.getState();
  if (!userId) {
    return [];
  }
  const snapshots = await Promise.all(
    sessionIds.map(async (sessionId) => {
      const snapshot = await getComposeSnapshot(userId, sessionId);
      await deleteComposeSnapshot(userId, sessionId);
      return snapshot;
    }),
  );
  return snapshots.filter((snapshot): snapshot is ComposeSnapshot =>
    Boolean(snapshot),
  );
};
